"use client";

import { useEffect, useId, type KeyboardEvent, type RefObject } from "react";
import { handleInlineComboboxKey } from "./inline-combobox-keyboard";
import { useInlineComboboxOptions } from "./inline-combobox-options";
import { useInlineComboboxState } from "./inline-combobox-state";
import type {
  InlineComboboxOption,
  InlineComboboxProps,
} from "./inline-combobox-types";

export type ResolvedInlineComboboxProps = InlineComboboxProps &
  Required<
    Pick<
      InlineComboboxProps,
      | "minSearchLength"
      | "isLoading"
      | "emptyText"
      | "disabled"
      | "size"
      | "variant"
    >
  >;

export function withInlineComboboxDefaults(
  props: InlineComboboxProps,
): ResolvedInlineComboboxProps {
  return {
    ...props,
    minSearchLength: props.minSearchLength ?? 0,
    isLoading: props.isLoading ?? false,
    emptyText: props.emptyText ?? "No results",
    disabled: props.disabled ?? false,
    size: props.size ?? "default",
    variant: props.variant ?? "field",
  };
}

function useFocusWhenOpen(
  open: boolean,
  inputRef: RefObject<HTMLInputElement | null>,
) {
  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open, inputRef]);
}

export function useInlineCombobox(
  props: ResolvedInlineComboboxProps,
  inputRef: RefObject<HTMLInputElement | null>,
) {
  const listId = useId();
  const state = useInlineComboboxState(props);
  const options = useInlineComboboxOptions({ ...props, ...state });
  const { open, setOpen, setQuery, setHighlightedValue } = state;

  const openWithQuery = (text: string) => {
    if (open || props.disabled) return;
    setQuery(text);
    setHighlightedValue(props.value);
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const openAndSeed = () => openWithQuery("");

  useFocusWhenOpen(open, inputRef);

  const close = () => setOpen(false);
  const choose = (option: InlineComboboxOption) => {
    if (option.value !== props.value) props.onSelect(option.value);
    close();
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) =>
    handleInlineComboboxKey(event, {
      ...options,
      open,
      openAndSeed,
      setQuery,
      setHighlightedValue,
      choose,
      close,
    });
  const closedText = props.displayValue ?? options.selectedOption?.label ?? "";

  return {
    ...state,
    ...options,
    listId,
    openAndSeed,
    openWithQuery,
    choose,
    handleKeyDown,
    closedText,
  };
}

export type InlineComboboxController = ReturnType<typeof useInlineCombobox>;
